// Payload size limits + push-error classification — the ONE shared home for
// the caps that are enforced in two places:
//   - client side: the sync controller checks `syncedRowBytes` against
//     MAX_DOC_BYTES before pushing, and the Supabase backend checks thumbnail
//     blobs against MAX_THUMBNAIL_BYTES before uploading;
//   - server side: supabase/migrations/0003_limits.sql adds CHECK constraints
//     on models.data / layouts.data and file_size_limit / allowed_mime_types
//     on the thumbnails bucket, with these SAME numeric values.
// Change a value here → change 0003_limits.sql too (the migration cites these
// constant names next to each literal so the pairing is greppable).
//
// The two sides don't measure identically — the client measures serialized
// UTF-8 JSON text, Postgres' pg_column_size measures the stored jsonb binary
// (possibly TOAST-compressed) — so the client check is a fast-path courtesy,
// not the guarantee. The guarantee is the classification below: a server-side
// constraint rejection is PERMANENT (retrying the identical payload can never
// succeed) and must be dropped from the outbox, not retried forever.

export const MAX_DOC_BYTES = 5 * 1024 * 1024; // 5 MiB — models/layouts `data` cap
export const MAX_THUMBNAIL_BYTES = 256 * 1024; // 256 KiB — thumbnails bucket cap

// Fields the backend strips from the row before push (thumbnail → Storage,
// reference photo → device-local, never uploaded). Shared with the backend's
// stripBlobs and with `syncedRowBytes` so the size measured is the size sent.
export const STRIPPED_BLOB_FIELDS = ["thumbnail", "reference"] as const;

let _encoder: TextEncoder | null = null;

// Byte size of a payload AS THE SERVER ROW WILL SEE IT: blob fields stripped,
// then UTF-8 byte length of the JSON text (names can be non-ASCII; the server
// measures bytes, so `.length` on the string would undercount).
export function syncedRowBytes(payload: unknown): number {
  if (payload === null || typeof payload !== "object") {
    const s = JSON.stringify(payload);
    return s === undefined ? 0 : s.length;
  }
  const p = { ...(payload as Record<string, unknown>) };
  for (const f of STRIPPED_BLOB_FIELDS) delete p[f];
  _encoder ??= new TextEncoder();
  return _encoder.encode(JSON.stringify(p)).byteLength;
}

// The user-facing message for an oversize document. The local copy is never
// touched — only the upload is refused.
export function docTooLargeMessage(payload: unknown): string {
  const n = (payload as { name?: unknown } | null)?.name;
  const label = typeof n === "string" && n ? `“${n}”` : "This document";
  const capMb = Math.round(MAX_DOC_BYTES / (1024 * 1024));
  return `${label} is too large to sync (over ${capMb} MB). It stays saved on this device, but won't upload to your account.`;
}

// A push failure that retrying can never fix (size cap, constraint violation,
// malformed payload). The controller drops the outbox entry and surfaces the
// message instead of retry-looping; everything else stays queued and retries.
export class PermanentSyncError extends Error {
  readonly permanent = true;
  constructor(message: string) {
    super(message);
    this.name = "PermanentSyncError";
  }
}

export function isPermanentSyncError(e: unknown): boolean {
  return (
    e instanceof PermanentSyncError ||
    (typeof e === "object" && e !== null && (e as { permanent?: unknown }).permanent === true)
  );
}

// Postgres SQLSTATE classes that mean the REQUEST is unacceptable, independent
// of server health — the identical retry can never succeed:
//   22 data exception · 23 integrity constraint violation (23514 = CHECK, our
//   size caps) · 42 syntax error / access rule violation · 54 program limits.
// Connection-class codes (08xxx), timeouts, and errors with no SQLSTATE at all
// (plain network failures) are transient and keep today's retry behavior.
export function isPermanentPushErrorCode(code: string | null | undefined): boolean {
  return typeof code === "string" && /^(22|23|42|54)/.test(code);
}
